import React, { useContext, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { App, Form, Input, DatePicker, Row, Col, InputNumber, Divider, Select, Button, message } from 'antd'
import { WarningOutlined } from '@ant-design/icons'
import { LoadingOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'
import dayjs, { Dayjs } from 'dayjs'

import Api from '@vacationtracker/shared/services/api'
import { notificationStore } from '../../context/notificationsContext/store'
import logger from '@vacationtracker/shared/functions/logger'
import IntlMessages from '../../util/IntlMessages'
import YesNoSwitch from '../yes-no-switch'
import { IHoliday, ILocationHolidayCreatedEvent, ILocationHolidayUpdatedEvent } from '@vacationtracker/shared/types/holidays'
import calendarHelper from '../months'
import { transferHolidayDates } from './transfer-holiday-dates'
import { IHolidaysByYear } from '../../types/locations'

type HolidayLength = 'SINGLE_DAY' | 'MULTI_DAY' | 'HALF_DAY'
type RecurringType = 'EXACT_DAY' | 'SPECIFIC_DAY'

interface ILocationHolidayFormProps {
  locationId: string
  locationName: string
  workWeek: number[]
  defaultWorkingHours: boolean
  existingLocationHolidays: IHolidaysByYear[]
  existingHolidayId?: string
}

interface IHolidayFormData {
  locationId: string
  name: string
  recurring: boolean
  startDate?: Dayjs
  endDate?: Dayjs
  holidayLength: HolidayLength
  transferIfFallsOnNonWorkingDay?: boolean
  recurringType?: RecurringType
  recurringStartDay?: number
  recurringStartMonth?: number
  recurringEndDay?: number
  recurringEndMonth?: number
  recurringDuration?: number
  recurringDaySet?: [number, number, number]
}

const setInitialHolidayFormData = (locationId: string, existingLocationHolidays: IHolidaysByYear[], existingHoliday?: IHoliday): IHolidayFormData => {

  let startDate: Dayjs | undefined = undefined
  let endDate: Dayjs | undefined = undefined
  let defaultHolidayLength: HolidayLength = 'SINGLE_DAY'
  let defaultRecurring = false
  let defaultRecurringType: RecurringType = 'EXACT_DAY'
  let defaultRecurringStartDay = 1
  let defaultRecurringStartMonth = 1
  let defaultRecurringEndDay = 31
  let defaultRecurringEndMonth = 12

  if (existingHoliday) {
    const rootRecurringStartHoliday = existingLocationHolidays
      .flatMap(year => year.holidays)
      .find(holiday => holiday.recurringId === existingHoliday.recurringId)
    let rootRecurringEndHoliday = rootRecurringStartHoliday
    if (rootRecurringStartHoliday?.multiDayId) {
      rootRecurringEndHoliday = existingLocationHolidays
        .flatMap(year => year.holidays)
        .sort((a, b) => dayjs(b.date).diff(dayjs(a.date)))
        .find(holiday => holiday.multiDayId === rootRecurringStartHoliday?.multiDayId)
    }

    //form is pre-populated with the root recurring holiday dates
    let rootRecurringStartDate = dayjs(rootRecurringStartHoliday?.transferOriginalDate || rootRecurringStartHoliday?.date || existingHoliday.date)
    let rootRecurringEndDate = dayjs(rootRecurringEndHoliday?.transferOriginalDate || rootRecurringEndHoliday?.date || existingHoliday.date)

    //it might be that they are sorted in reverse order due to the transfer of non working day
    if (rootRecurringStartDate.isAfter(rootRecurringEndDate)) {
      const temp = rootRecurringStartDate
      rootRecurringStartDate = rootRecurringEndDate
      rootRecurringEndDate = temp
    }

    defaultRecurringStartDay = rootRecurringStartDate.date()
    defaultRecurringStartMonth = rootRecurringStartDate.month() + 1
    defaultRecurringEndDay = rootRecurringEndDate.date()
    defaultRecurringEndMonth = rootRecurringEndDate.month() + 1
    defaultHolidayLength = existingHoliday.multiDayId ? 'MULTI_DAY' : existingHoliday?.isHalfDay ? 'HALF_DAY' : 'SINGLE_DAY'
    defaultRecurring = existingHoliday.recurring || false
    defaultRecurringType = existingHoliday.recurringRules?.specificDaySet ? 'SPECIFIC_DAY' : 'EXACT_DAY'
    startDate = dayjs(existingHoliday.date)
    if (!existingHoliday.recurring) {
      if (existingHoliday.multiDayId) {
        const multiDayHolidays = existingLocationHolidays
          .flatMap(year => year.holidays)
          .filter(h => h.multiDayId === existingHoliday?.multiDayId)
        
        startDate = dayjs(multiDayHolidays[0].date)
        endDate = dayjs(multiDayHolidays[multiDayHolidays.length - 1].date)
      }
    }
  }
  return {
    locationId,
    name: existingHoliday?.name || '',
    startDate,
    endDate,
    transferIfFallsOnNonWorkingDay: existingHoliday?.transferIfFallsOnNonWorkingDay || false,
    recurring: defaultRecurring,
    holidayLength: defaultHolidayLength,
    recurringType: defaultRecurringType,
    recurringStartDay: defaultRecurringStartDay,
    recurringStartMonth: defaultRecurringStartMonth,
    recurringEndDay: defaultRecurringEndDay,
    recurringEndMonth: defaultRecurringEndMonth,
    recurringDaySet: existingHoliday?.recurringRules?.specificDaySet || [1, 1, 1],
    recurringDuration: existingHoliday?.recurringRules?.specificDayDuration || 2,
  }
}

const LocationHolidayForm = ({locationId, locationName, workWeek, defaultWorkingHours, existingLocationHolidays, existingHolidayId}: ILocationHolidayFormProps) => {

  const history = useHistory()
  const { formatMessage } = useIntl()
  const [form] = Form.useForm()
  const { notification } = App.useApp()
  const { actionNotifications, setActionNotifications } = useContext(notificationStore)
  const [btnLoading, setBtnLoading] = useState(false)

  const existingHoliday: IHoliday | undefined = existingLocationHolidays
    ?.find(year => year.holidays.find(h => h.id === existingHolidayId))
    ?.holidays.find(h => h.id === existingHolidayId)

  if (existingHoliday && !existingHoliday) {
    message.error('Holiday not found')
    window.location.href = '/app/settings/locations'
    throw new Error('Holiday not found')
  }
  
  const disableEditingName = existingHoliday?.recurring && existingHoliday?.type === 'CUSTOM'
  const disableEditingRecurrence = existingHoliday?.recurring || existingHoliday?.type === 'LIB'
  const disableEditingForm = existingHoliday?.recurring && existingHoliday?.type === 'CUSTOM'

  const [holidayFormData, setHolidayFormData] = useState<IHolidayFormData>(setInitialHolidayFormData(locationId, existingLocationHolidays, existingHoliday))

  const backToHolidays = () => {
    history.push(`/app/settings/locations/${locationId}/holidays`)
  }

  const getReferenceYear = (): number => {
    const addYears = existingHolidayId ? 1 : 0
    return new Date().getFullYear() + addYears
  }

  const isLeapYear = (year: number): boolean => {
    return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0)
  }

  const getMaxDaysForMonth = (month?: number): number => {
    if (typeof month !== 'number' || !Number.isInteger(month) || month < 1 || month > 12) {
      throw new Error(`Invalid month value: ${month}. Month must be an integer between 1 and 12.`)
    }
    const monthLengths = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]
    const idx = month - 1
    
    if (idx === 1) {
      const year = getReferenceYear()
      return isLeapYear(year) ? 29 : 28
    }
    return monthLengths[idx]
  }

  const getMonthDaysOptions = (month?: number) => {
    const max = getMaxDaysForMonth(month)
    return Array.from({ length: max }, (_, i) => ({ value: i + 1 }))
  }

  const checkOverlappingHolidays = (startDate: string, endDate: string): IHoliday | undefined => {
    return existingLocationHolidays?.flatMap(year => year.holidays)
      .find(h => h.id !== existingHolidayId && 
        (!existingHoliday?.recurringId || h.recurringId !== existingHoliday.recurringId) && 
        (!existingHoliday?.multiDayId || h.multiDayId !== existingHoliday.multiDayId) &&
        (dayjs(h.date).isSame(startDate, 'day') || dayjs(h.date).isSame(endDate, 'day')))
  }

  const prepareApiData = (values: IHolidayFormData): Partial<ILocationHolidayCreatedEvent>|Partial<ILocationHolidayUpdatedEvent> => {
    let startDate = values.startDate
    let endDate = values.endDate || values.startDate
    if (!values.recurring) {
      endDate = values.holidayLength === 'MULTI_DAY' ? values.endDate : values.startDate
    } else {
      if (values.recurringType === 'EXACT_DAY') {
        startDate = dayjs().month(Number(values.recurringStartMonth) - 1).date(values.recurringStartDay as number)
        endDate = values.holidayLength === 'MULTI_DAY' ? dayjs().month(Number(values.recurringEndMonth) - 1).date(values.recurringEndDay as number) : startDate
      }
      if (values.recurringType === 'SPECIFIC_DAY') {
        // specific day rules will set the exact date -> backend will calculate the rest
        // find holiday date according to specific day rules(practically needed just to check overlapping holidays on frontend): [weekOfMonth, dayOfWeek, month]
        const month = Number(values.recurringDaySet?.[2]) - 1 //recurring rules save months as 1-12, but dayjs as 0-11
        const inputDay = Number(values.recurringDaySet?.[1]) // 1=Mon, 7=Sun
        const dayOfWeek = inputDay === 7 ? 0 : inputDay // If Sunday (7), convert to 0, otherwise keep as is - recurring rules save days as 1-7, but dayjs as 0-6
        const weekOfMonth = Number(values.recurringDaySet?.[0])
        startDate = dayjs().month(month).startOf('month')
        while (startDate.day() !== dayOfWeek) {
          startDate = startDate.add(1, 'day')
        }
        startDate = startDate.add(weekOfMonth - 1, 'week')
        endDate = startDate
      }
    }
    return {
      eventType: existingHolidayId ? 'LOCATION_HOLIDAY_UPDATED' : 'LOCATION_HOLIDAY_CREATED',
      eventGroup: 'LOCATION',
      locationId,
      name: values.name,
      startDate: startDate?.format('YYYY-MM-DD'),
      endDate: endDate?.format('YYYY-MM-DD'),
      isHalfDay: values.holidayLength === 'HALF_DAY',
      recurring: values.recurring,
      ...values.recurring ? { 
        recurringRules: {
          ...values.recurringType === 'EXACT_DAY' ? { exactDate: true } : {},
          ...values.recurringType === 'SPECIFIC_DAY' ? { specificDaySet: values.recurringDaySet, specificDayDuration: values.recurringDuration } : {},
        },
        transferIfFallsOnNonWorkingDay: values.transferIfFallsOnNonWorkingDay,
      } : {},
      ...existingHolidayId ? { id: existingHolidayId } : {},
    }
  }

  const onFinish = () => {
    form
      .validateFields()
      .then(async (values: IHolidayFormData) => {
        try {
          const apiData = prepareApiData(values)
          const overlappingHoliday = checkOverlappingHolidays(apiData.startDate as string, apiData.endDate as string)
          if (overlappingHoliday) {
            notification.error({ 
              message: formatMessage(
                { id: 'components.createHolidaysForm.createHolidayErrorOverlap' },
                { holidayName: overlappingHoliday.name }
              ),
              duration: 0,
            })
            return
          }
          if (apiData.endDate && dayjs(apiData.startDate).isAfter(dayjs(apiData.endDate))) {
            notification.error({ message: formatMessage({ id: 'components.createHolidaysForm.createHolidayErrorEndDate' }), duration: 0 })
            return
          }

          setBtnLoading(true)
          const response = await Api.post('/core/event', apiData)

          notification.open({
            key: response.correlationId,
            message: formatMessage({ id: existingHolidayId ? 'components.createHolidaysForm.updateInProgress' : 'components.createHolidaysForm.createInProgress' }),
            icon: (<LoadingOutlined />),
            duration: 0,
          })

          setActionNotifications([ ...actionNotifications, response.correlationId ])
          setTimeout(() => { backToHolidays() }, 2000)
        } catch (error) {
          setBtnLoading(false)
          notification.error({
            message: formatMessage({ id: 'components.createHolidaysForm.createHolidayError' }),
            description: error.message || formatMessage({ id: 'error.generic' }),
            duration: 0,
          })
        }
      })
      .catch(info => {
        logger.warning('Validate Failed:', info)
        setBtnLoading(false)
      })
  }

  const getNextRecurringHolidayDate = () => {
    const addYears = existingHolidayId ? 1 : 0
    const year = new Date().getFullYear() + addYears
    let startDate: Date | undefined = undefined
    let endDate: Date | undefined = undefined
    
    // Get initial dates
    if (holidayFormData.recurringType === 'EXACT_DAY' && holidayFormData.recurringStartMonth && holidayFormData.recurringStartDay) {
      startDate = new Date(year, holidayFormData.recurringStartMonth - 1, holidayFormData.recurringStartDay)
      
      if (holidayFormData.holidayLength === 'MULTI_DAY' && holidayFormData.recurringEndDay && holidayFormData.recurringEndMonth) {
        endDate = new Date(year, holidayFormData.recurringEndMonth - 1, holidayFormData.recurringEndDay)
      }
    }

    if (holidayFormData.recurringType === 'SPECIFIC_DAY' && holidayFormData.recurringDaySet) {
      const [weekNumber, dayOfWeek, month] = holidayFormData.recurringDaySet
      const firstDayOfMonth = new Date(year, month - 1, 1)
      const firstDayOfWeek = firstDayOfMonth.getDay()
      const daysToAdd = ((weekNumber - 1) * 7) + ((dayOfWeek - firstDayOfWeek + 7) % 7)
      
      startDate = new Date(year, month - 1, 1 + daysToAdd)
      
      if (holidayFormData.holidayLength === 'MULTI_DAY') {
        endDate = new Date(startDate)
        endDate.setDate(startDate.getDate() + Number(holidayFormData.recurringDuration) - 1)
      }
    }

    if (!startDate) return ''

    if (holidayFormData.transferIfFallsOnNonWorkingDay) {
      const transferedDatesResult = transferHolidayDates(workWeek, startDate, endDate)
      startDate = transferedDatesResult.startDate
      endDate = transferedDatesResult.endDate
    }

    return `${startDate.toDateString()} ${endDate ? `- ${endDate.toDateString()}` : ''}`
  }

  const generateRecurringSummary = () => {
    if (!holidayFormData.recurring) return null
    return <div>
      <IntlMessages id="components.createHolidaysForm.recurringSummary" />:&nbsp;
      {holidayFormData.recurringType === 'EXACT_DAY' && 
        <IntlMessages id="components.createHolidaysForm.recurringSummaryExactDay" values={{
          date: <>
            {<IntlMessages id={calendarHelper.months.find(month => month.value === holidayFormData.recurringStartMonth)?.label} />} {holidayFormData.recurringStartDay}
            {holidayFormData.holidayLength === 'MULTI_DAY' && holidayFormData.recurringEndDay && <>
              {' - '}
              <IntlMessages id={calendarHelper.months.find(month => month.value === holidayFormData.recurringEndMonth)?.label} />
              {' '}
              {holidayFormData.recurringEndDay}
            </>}
          </>,
        }} />
      }
      {holidayFormData.recurringType === 'SPECIFIC_DAY' && 
        <IntlMessages id="components.createHolidaysForm.recurringSummarySpecificDay" values={{ 
          weekNumber: <span style={{ textTransform: 'lowercase' }}>
            <IntlMessages id={calendarHelper.monthWeeks.find(week => week.value === holidayFormData.recurringDaySet?.[0])?.label} />
          </span>,
          dayOfWeek: <IntlMessages id={calendarHelper.days.find(day => day.value === holidayFormData.recurringDaySet?.[1])?.label} />,
          month: <IntlMessages id={calendarHelper.months.find(month => month.value === holidayFormData.recurringDaySet?.[2])?.label} />,
          duration: holidayFormData.holidayLength === 'MULTI_DAY' ? holidayFormData.recurringDuration : holidayFormData.holidayLength === 'HALF_DAY' ? 'halfDay' : 1,
        }} />
      }
      {holidayFormData.transferIfFallsOnNonWorkingDay && <>, <IntlMessages id="components.createHolidaysForm.recurringSummaryTransfer" /></>}
      .<br/>
      <IntlMessages id="components.createHolidaysForm.recurringSummaryNextDate" values={{ date: getNextRecurringHolidayDate() }} />
    </div>
  }

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        name="holidayForm"
        onFinish={onFinish}
        className="holiday-form"
        initialValues={holidayFormData}
        disabled={disableEditingForm}
        requiredMark="optional"
        scrollToFirstError
        labelWrap
        size="large"
      >
        <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
          <Col xxl={8} xl={8} lg={6} md={6} sm={24} xs={24}>
            <h2><IntlMessages id="components.createHolidaysForm.createIntro1Title" /></h2>
            <p><IntlMessages id="components.createHolidaysForm.createIntro1Description" /></p>
          </Col>
          <Col xxl={16} xl={16} lg={18} md={18} sm={24} xs={24}>
            <Form.Item
              name="name"
              label={<IntlMessages id="app.name" />}
              rules={[
                {
                  required: true,
                  message: <IntlMessages id="form.nameRequired" />,
                },
              ]}
            >
              <Input
                disabled={disableEditingName}
                placeholder={formatMessage({ id: 'components.createHolidaysForm.name' })}
              />
            </Form.Item>
            <Form.Item
              name="locationId"
              label={<IntlMessages id="components.createHolidaysForm.locations" />}
              rules={[
                {
                  required: true,
                  message: <IntlMessages id="form.inputRequired" />,
                },
              ]}
            >
              <Select disabled>
                <Select.Option value={locationId}>{locationName}</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Divider />
        <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
          <Col xxl={8} xl={8} lg={6} md={6} sm={24} xs={24}>
            <h2><IntlMessages id="components.createHolidaysForm.createIntro2Title" /></h2>
            <p><IntlMessages id="components.createHolidaysForm.createIntro2Description" /></p>
          </Col>
          <Col xxl={16} xl={16} lg={18} md={18} sm={24} xs={24}>
            {existingHoliday?.type === 'LIB' && 
              <div style={{ marginBottom: '10px' }}><WarningOutlined /> <IntlMessages id="components.createHolidaysForm.holidayFormRecurrenceDisabledWarning" /></div>
            }
            <YesNoSwitch
              name="recurring"
              disableYes={existingHoliday?.type === 'LIB' || Boolean(existingHoliday?.date && dayjs(existingHoliday.date).year() < new Date().getFullYear())}
              onChange={(value) => setHolidayFormData({ ...holidayFormData, recurring: value })}
              label={<IntlMessages id="components.createHolidaysForm.recurring" />}
              required
              disabled={existingHoliday?.type === 'CUSTOM' && disableEditingRecurrence}
              tooltip={<IntlMessages id="holidays.importHolidaysAutomaticallyTooltip" />}
            />
            <Form.Item
              name="holidayLength"
              rules={[
                {
                  required: true,
                  message: <IntlMessages id="form.inputRequired" />,
                },
              ]}
              label={<IntlMessages id="components.createHolidaysForm.holidayType" />}
            >
              <Select disabled={disableEditingRecurrence} onChange={(value) => setHolidayFormData({ ...holidayFormData, holidayLength: value })}>
                <Select.Option value={'SINGLE_DAY'}><IntlMessages id="components.createHolidaysForm.singleDayHoliday" /></Select.Option>
                <Select.Option value={'MULTI_DAY'}><IntlMessages id="components.createHolidaysForm.multiDayHoliday" /></Select.Option>
                {defaultWorkingHours && <Select.Option value={'HALF_DAY'}><IntlMessages id="components.createHolidaysForm.halfDayHoliday" /></Select.Option>}
              </Select>
            </Form.Item>

            {(existingHoliday?.type === 'LIB' || !holidayFormData.recurring) && <Form.Item
              name="startDate"
              label={holidayFormData.holidayLength === 'MULTI_DAY' ? <IntlMessages id="users.startDate" /> : <IntlMessages id="components.leaveForm.date" />}
              rules={[
                {
                  required: true,
                  message: <IntlMessages id="components.addHolidayForm.date.error" />,
                },
              ]}
            >
              <DatePicker disabled={disableEditingRecurrence} disabledDate={(current) => current && ((current.year() > new Date().getFullYear() + 1) || current.year() < 2021)}/>
            </Form.Item>}
            {!holidayFormData.recurring && holidayFormData.holidayLength === 'MULTI_DAY' && (
              <Form.Item
                name="endDate"
                label={<IntlMessages id="users.endDate" />}
                rules={[
                  {
                    required: true,
                    message: <IntlMessages id="components.addHolidayForm.date.error" />,
                  },
                ]}
              >
                <DatePicker disabled={disableEditingRecurrence} />
              </Form.Item>
            )}
            {holidayFormData.recurring && existingHoliday?.type !== 'LIB' && <div>
              <Form.Item
                name="recurringType"
                rules={[
                  {
                    required: true,
                    message: <IntlMessages id="form.inputRequired" />,
                  },
                ]}
                label={<IntlMessages id="components.createHolidaysForm.recurringType" />}
              >
                <Select disabled={disableEditingRecurrence} onChange={(value) => setHolidayFormData({ ...holidayFormData, recurringType: value })}>
                  <Select.Option value={'EXACT_DAY'}><IntlMessages id="components.createHolidaysForm.recurringTypeExactDay" /></Select.Option>
                  <Select.Option value={'SPECIFIC_DAY'}><IntlMessages id="components.createHolidaysForm.recurringTypeSpecificDay" /></Select.Option>
                </Select>
              </Form.Item>
              {holidayFormData.recurringType === 'EXACT_DAY' && <div>
                <Form.Item
                  label={<IntlMessages id="components.createHolidaysForm.recurringStartDay" />}
                >
                  <Row gutter={8}>
                    <Col span={12}>
                      <Form.Item
                        name="recurringStartMonth"
                        noStyle
                      >
                        <Select 
                          disabled={disableEditingRecurrence}
                          onChange={(value) => {
                            const next = { ...holidayFormData, recurringStartMonth: value }
                            const max = getMaxDaysForMonth(value)
                            const currentDay = form.getFieldValue('recurringStartDay')
                            if (currentDay && currentDay > max) {
                              form.setFieldsValue({ recurringStartDay: undefined })
                              next.recurringStartDay = undefined
                            }
                            setHolidayFormData(next)
                          }}
                          placeholder={<IntlMessages id="components.notificationForm.month" />}
                        >
                          {calendarHelper.months.map(option => (
                            <Select.Option key={option.value} value={option.value}>
                              <IntlMessages id={option.label} />
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="recurringStartDay"
                        noStyle
                      >
                        <Select 
                          disabled={disableEditingRecurrence}
                          onChange={(value) => setHolidayFormData({ ...holidayFormData, recurringStartDay: value })}
                          placeholder={<IntlMessages id="components.notificationForm.day" />}
                        >
                          {getMonthDaysOptions(holidayFormData.recurringStartMonth).map(option => (
                            <Select.Option key={option.value} value={option.value}>
                              {option.value}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                </Form.Item>
                {holidayFormData.holidayLength === 'MULTI_DAY' && <Form.Item
                  label={<IntlMessages id="components.createHolidaysForm.recurringEndDay" />}
                >
                  <Row gutter={8}>
                    <Col span={12}>
                      <Form.Item
                        name="recurringEndMonth"
                        noStyle
                      >
                        <Select 
                          disabled={disableEditingRecurrence}
                          onChange={(value) => {
                            const next = { ...holidayFormData, recurringEndMonth: value }
                            const max = getMaxDaysForMonth(value)
                            const currentDay = form.getFieldValue('recurringEndDay')
                            if (currentDay && currentDay > max) {
                              form.setFieldsValue({ recurringEndDay: undefined })
                              next.recurringEndDay = undefined
                            }
                            setHolidayFormData(next)
                          }}
                          placeholder={<IntlMessages id="components.notificationForm.month" />}
                        >
                          {calendarHelper.months.map(option => (
                            <Select.Option key={option.value} value={option.value}>
                              <IntlMessages id={option.label} />
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="recurringEndDay"
                        noStyle
                      >
                        <Select 
                          disabled={disableEditingRecurrence}
                          onChange={(value) => setHolidayFormData({ ...holidayFormData, recurringEndDay: value })}
                          placeholder={<IntlMessages id="components.notificationForm.day" />}
                        >
                          {getMonthDaysOptions(holidayFormData.recurringEndMonth).map(option => (
                            <Select.Option key={option.value} value={option.value}>
                              {option.value}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                </Form.Item>}
              </div>}
              {holidayFormData.recurringType === 'SPECIFIC_DAY' && <div>
                <Form.Item
                  name="recurringDaySet"
                  label={<IntlMessages id="components.createHolidaysForm.recurringDate" />}
                >
                  <Row gutter={8}>
                    <Col span={8}>
                      <Form.Item name={['recurringDaySet', 0]} noStyle rules={[
                        {
                          required: true,
                          message: <IntlMessages id="form.inputRequired" />,
                        },
                      ]}>
                        <Select disabled={disableEditingRecurrence} onChange={(value) => setHolidayFormData({ 
                          ...holidayFormData, 
                          recurringDaySet: [value, holidayFormData.recurringDaySet?.[1] || 1, holidayFormData.recurringDaySet?.[2] || 1],
                        })} placeholder={<IntlMessages id="components.notificationForm.week" />}>
                          {calendarHelper.monthWeeks.map(option => (
                            <Select.Option key={option.value} value={option.value}>
                              <IntlMessages id={option.label} />
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name={['recurringDaySet', 1]} noStyle rules={[
                        {
                          required: true,
                          message: <IntlMessages id="form.inputRequired" />,
                        },
                      ]}>
                        <Select 
                          disabled={disableEditingRecurrence}
                          onChange={(value) => setHolidayFormData({ 
                            ...holidayFormData, 
                            recurringDaySet: [holidayFormData.recurringDaySet?.[0] || 1, value, holidayFormData.recurringDaySet?.[2] || 1],
                          })} 
                          placeholder={<IntlMessages id="components.notificationForm.day" />}
                        >
                          {calendarHelper.days.map(option => (
                            <Select.Option key={option.value} value={option.value}>
                              <IntlMessages id={option.label} />
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name={['recurringDaySet', 2]} noStyle rules={[
                        {
                          required: true,
                          message: <IntlMessages id="form.inputRequired" />,
                        },
                      ]}>
                        <Select
                          disabled={disableEditingRecurrence}
                          onChange={(value) => setHolidayFormData({ 
                            ...holidayFormData, 
                            recurringDaySet: [holidayFormData.recurringDaySet?.[0] || 1, holidayFormData.recurringDaySet?.[1] || 1, value],
                          })} 
                          placeholder={<IntlMessages id="components.notificationForm.month" />}
                        >
                          {calendarHelper.months.map(option => (
                            <Select.Option key={option.value} value={option.value}>
                              <IntlMessages id={option.label} />
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                </Form.Item>
                {holidayFormData.holidayLength === 'MULTI_DAY' && <Form.Item
                  name="recurringDuration"
                  style={{ width: '130px' }}
                  label={<IntlMessages id="components.tables.duration" />}
                  rules={[
                    {
                      required: true,
                      message: <IntlMessages id="form.durationRequired" />,
                    },
                  ]}
                >
                  <InputNumber
                    min={2}
                    addonAfter={<IntlMessages id="app.days" />}
                    disabled={disableEditingRecurrence}
                    defaultValue={2}
                    onChange={(value) => setHolidayFormData({ ...holidayFormData, recurringDuration: Number(value) })} 
                  />
                </Form.Item>}
              </div>}
              <YesNoSwitch
                disabled={disableEditingRecurrence}
                onChange={(value) => setHolidayFormData({ ...holidayFormData, transferIfFallsOnNonWorkingDay: value })}
                name="transferIfFallsOnNonWorkingDay"
                label={<IntlMessages id="components.createHolidaysForm.transferIfFallsOnNonWorkingDay" />}
              />
            </div>}
          </Col>
        </Row>
        <Divider />
        <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
          <Col span={24}>
            {disableEditingForm && <div style={{ textAlign: 'right', maxWidth: '100%' }}>
              <WarningOutlined /> <IntlMessages id="components.createHolidaysForm.holidayCustomRecurringWarning" />
            </div>}
            <Form.Item style={{ textAlign: 'right', maxWidth: '100%' }}>
              <Button style={{ marginRight: 10 }} onClick={backToHolidays}><IntlMessages id="app.cancel" /></Button>
              <Button type="primary" htmlType="submit" loading={btnLoading}><IntlMessages id="app.save" /></Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <Row>
        <Col xxl={8} xl={8} lg={6} md={6} sm={24} xs={24}>
        </Col>
        <Col xs={24} sm={24} md={16} lg={16} xl={12}>
          <div className='vt-primary-color' style={{ textAlign: 'center' }}>{generateRecurringSummary()}</div>
        </Col>
      </Row>
    </>
  )
}

export default LocationHolidayForm